'use client'

import React, { useEffect, useState, useRef } from 'react'
import { handleProductImageFallback } from 'utils/imageFallback'
import { Box, Text } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { getBackgroundImageUrl } from 'app/utils/helpers/helpers'
import { Item } from 'appTypes/mapper'

type Props = {
  product: Item | undefined
}

const ProductImageView = ({ product }: Props) => {
  const { t } = useTranslation('common')
  const sortedImages =
    product?.products[0] && [...(product?.products[0].images ?? [])].sort((a, b): number => a.id - b.id)

  const [currentMediumUrl, setCurrentMediumUrl] = useState<string | undefined>(sortedImages?.[0]?.mediumUrl ?? '')
  const [mediumErrorLevel, setMediumErrorLevel] = useState(0)
  const [isVisible, setIsVisible] = useState(false)

  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setCurrentMediumUrl(sortedImages?.[0]?.mediumUrl)
  }, [product])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true)
            observer.disconnect()
          }
        })
      },
      { threshold: 0.1 },
    )

    if (ref.current) observer.observe(ref.current)

    return () => observer.disconnect()
  }, [])

  const handleMediumImageErrorFallback = (index: number) => {
    if (!sortedImages || !sortedImages[index]) {
      return
    }

    handleProductImageFallback(
      sortedImages[index],
      currentMediumUrl,
      mediumErrorLevel,
      setCurrentMediumUrl,
      setMediumErrorLevel,
    )
  }

  return (
    <Box
      width="100%"
      height="100%"
      position="relative"
      backgroundColor="#fff"
      borderRadius="big"
      borderBottomRadius="none"
      overflow="hidden"
      borderBottomWidth="1px"
      borderBottomColor="gray.a50"
    >
      <Box
        ref={ref}
        width="100%"
        height="100%"
        backgroundImage={isVisible && currentMediumUrl ? getBackgroundImageUrl(currentMediumUrl) : 'none'}
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundRepeat="no-repeat"
        onError={() => handleMediumImageErrorFallback(0)}
      >
        {!isVisible && (
          <Text
            fontSize="sm"
            color="gray.400"
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
          >
            {t('loading')}
          </Text>
        )}
      </Box>
    </Box>
  )
}

export default ProductImageView
